import { motion } from "framer-motion";
import {
  Eye, PencilSimple, Trash, PushPin, Heart, CopySimple,
  CaretDown, CalendarBlank, X, ArrowsClockwise, PaperPlaneTilt,
  Clock, Check, Warning, ChartLineUp
} from "@phosphor-icons/react";
import { STATUS_CONFIG, STATUS_ORDER, isMultiSectionDraft } from "@/lib/generateConstants";

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function HistoryCard({
  item, idx, isClient,
  statusDropdownOpen, onStatusDropdownToggle,
  onStatusChange, onPublishDate,
  onTogglePin, onToggleFavorite, onScore, scoringId,
  onView, onEdit, onRepurpose, onDuplicate, onShare, onDelete,
}) {
  const getStatusOptions = () => isClient ? ["in_review", "approved"] : STATUS_ORDER;

  return (
    <motion.div
      key={item.history_id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05 }}
      className={`bg-[var(--bg-card)] border rounded-lg p-4 hover:border-[var(--border-strong)] transition-colors group ${
        item.is_pinned ? "border-[#FF4400]/30 bg-[var(--bg-card)]/80" : "border-[var(--border)]"
      }`}
      data-testid={`history-item-${item.history_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {item.is_pinned && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--accent-subtle)] text-[#FF4400] font-['JetBrains_Mono']">
                <PushPin size={10} weight="fill" />PINNED
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_CONFIG[item.status || "draft"]?.color} ${STATUS_CONFIG[item.status || "draft"]?.border}`} data-testid={`status-badge-${item.history_id}`}>
              {STATUS_CONFIG[item.status || "draft"]?.label}
            </span>
            {item.client_approval && item.client_approval.status !== "not_sent" && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                item.client_approval.status === "approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                : item.client_approval.status === "changes_requested" ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                : "bg-gray-500/10 text-gray-400 border-gray-500/20"
              }`} data-testid={`client-status-${item.history_id}`} title={item.client_approval.client_note ? `Client note: ${item.client_approval.client_note}` : ""}>
                {item.client_approval.status === "approved" && <><Check size={10} weight="bold" /> Client approved</>}
                {item.client_approval.status === "changes_requested" && <><Warning size={10} /> Changes requested</>}
                {item.client_approval.status === "pending" && <><Clock size={10} /> Awaiting review</>}
              </span>
            )}
            {item.brief_id && <span className="px-2 py-0.5 text-[9px] rounded-full bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/20 font-medium">From Brief</span>}
            <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-muted)]">{formatDate(item.created_at)}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-[var(--accent-subtle)] text-[#FF4400]">{item.content_type}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-[var(--bg-hover)] text-[var(--text-secondary)]">{item.platform}</span>
            {isMultiSectionDraft(item.generated_draft, item.content_type) && (
              <span className="px-2 py-0.5 rounded text-xs bg-[var(--success)]/10 text-[var(--success)] font-['JetBrains_Mono']">MULTI-SECTION</span>
            )}
          </div>
          <h3 className="text-[var(--text-primary)] font-medium truncate">{item.title || item.topic}</h3>
          <p className="text-sm text-[var(--text-muted)] truncate mt-1">
            {(item.generated_draft || "").replace(/#{1,6}\s/g, "").replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1").replace(/---/g, "").replace(/\n/g, " ").substring(0, 100)}...
          </p>
          <span className="text-xs text-[var(--text-muted)] mt-1 inline-block">{item.ai_model_used}</span>
          {item.quality_score && (
            <button onClick={() => onScore(item.history_id)} className="inline-flex items-center gap-1 ml-2 mt-1 text-xs cursor-pointer" data-testid={`score-badge-${item.history_id}`}>
              <span className={`px-1.5 py-0.5 rounded-md font-['JetBrains_Mono'] font-semibold ${
                item.quality_score >= 8 ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                item.quality_score >= 6 ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" :
                item.quality_score >= 4 ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                "bg-red-500/10 text-red-400 border border-red-500/20"
              }`}>{item.quality_score}/10</span>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 shrink-0">
          {/* Status selector */}
          <div className="relative" data-testid={`status-selector-${item.history_id}`}>
            <button
              onClick={() => onStatusDropdownToggle(item.history_id)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs border transition-colors ${STATUS_CONFIG[item.status || "draft"]?.color} ${STATUS_CONFIG[item.status || "draft"]?.border}`}
              data-testid={`status-dropdown-trigger-${item.history_id}`}
            >
              {STATUS_CONFIG[item.status || "draft"]?.label}<CaretDown size={12} />
            </button>
            {statusDropdownOpen === item.history_id && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-raised)] border border-[var(--border-strong)] rounded-lg shadow-xl py-1 min-w-[140px]" data-testid={`status-dropdown-menu-${item.history_id}`}>
                {getStatusOptions().map((s) => (
                  <button key={s} onClick={() => onStatusChange(item, s)} className={`w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[var(--bg-hover)] ${(item.status || "draft") === s ? "text-[#FF4400] font-medium" : "text-[var(--text-secondary)]"}`} data-testid={`status-option-${s}-${item.history_id}`}>
                    {STATUS_CONFIG[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Publish date */}
          <div className="flex items-center gap-1" data-testid={`publish-date-${item.history_id}`}>
            <label className="relative cursor-pointer p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors" title="Set publish date">
              <CalendarBlank size={18} className={item.planned_publish_date ? "text-[#FF4400]" : ""} />
              <input type="date" value={item.planned_publish_date || ""} onChange={(e) => onPublishDate(item, e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" data-testid={`date-input-${item.history_id}`} />
            </label>
            {item.planned_publish_date && (
              <span className="flex items-center gap-1 text-xs text-[#FF4400] font-['JetBrains_Mono']">
                {formatShortDate(item.planned_publish_date)}
                <button onClick={() => onPublishDate(item, "")} className="p-0.5 rounded hover:bg-red-400/10 text-[var(--text-muted)] hover:text-red-400 transition-colors" data-testid={`clear-date-${item.history_id}`} title="Clear date"><X size={12} /></button>
              </span>
            )}
          </div>

          {/* Action buttons */}
          <button onClick={() => onTogglePin(item)} className={`p-2.5 sm:p-2 rounded-md transition-colors ${item.is_pinned ? "text-[#FF4400] bg-[var(--accent-subtle)]" : "text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)]"}`} data-testid={`history-pin-${item.history_id}`} title={item.is_pinned ? "Unpin" : "Pin to top"}>
            <PushPin size={18} weight={item.is_pinned ? "fill" : "regular"} />
          </button>
          <button onClick={() => onToggleFavorite(item)} className={`p-2.5 sm:p-2 rounded-md transition-colors ${item.is_favorited ? "text-rose-400 bg-rose-400/10" : "text-[var(--text-muted)] hover:text-rose-400 hover:bg-rose-400/10"}`} data-testid={`history-favorite-${item.history_id}`} title={item.is_favorited ? "Unfavorite" : "Favorite"}>
            <Heart size={18} weight={item.is_favorited ? "fill" : "regular"} />
          </button>
          <button onClick={() => onScore(item.history_id)} disabled={scoringId === item.history_id} className={`p-2.5 sm:p-2 rounded-md transition-colors ${item.quality_score ? "text-emerald-400 bg-emerald-500/10" : "text-[var(--text-muted)] hover:text-amber-400 hover:bg-amber-400/10"}`} data-testid={`history-score-${item.history_id}`} title={item.quality_score ? `Score: ${item.quality_score}/10` : "Score content"}>
            {scoringId === item.history_id ? <span className="w-[18px] h-[18px] border-2 border-[#FF4400] border-t-transparent rounded-full animate-spin inline-block" /> : <ChartLineUp size={18} weight={item.quality_score ? "fill" : "regular"} />}
          </button>
          <button onClick={() => onView(item)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors" data-testid={`history-view-${item.history_id}`} title="View"><Eye size={18} /></button>
          <button onClick={() => onEdit(item)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--success)] hover:bg-[var(--success)]/10 transition-colors" data-testid={`history-edit-${item.history_id}`} title="Load to Editor"><PencilSimple size={18} /></button>
          <button onClick={() => onRepurpose(item)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--success)] hover:bg-[var(--success)]/10 transition-colors" data-testid={`history-repurpose-${item.history_id}`} title="Repurpose"><ArrowsClockwise size={18} /></button>
          <button onClick={() => onDuplicate(item)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-emerald-400 hover:bg-emerald-400/10 transition-colors" data-testid={`history-duplicate-${item.history_id}`} title="Quick Duplicate"><CopySimple size={18} /></button>
          <button onClick={() => onShare(item)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors" data-testid={`history-share-${item.history_id}`} title="Share for Approval"><PaperPlaneTilt size={18} /></button>
          <button onClick={() => onDelete(item.history_id)} className="p-2.5 sm:p-2 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`history-delete-${item.history_id}`} title="Delete"><Trash size={18} /></button>
        </div>
      </div>
    </motion.div>
  );
}
